<template>
  <div class="jobs-dashboard">
    <!-- Gated state for non-premium accounts -->
    <div v-if="!premium" class="jr-card gate-card">
      <div class="gate-body">
        <div class="gate-eyebrow">Premium</div>
        <h2 class="gate-title">Job management reporting is a Premium feature</h2>
        <p class="gate-text">See which jobs are moving, which are stuck, and what reviewers are saying, all in one report.</p>
        <ul class="gate-features">
          <li v-for="feature in features" :key="feature">
            <img src="img/check-circle.svg" alt="" />
            {{ feature }}
          </li>
        </ul>
        <div class="gate-actions">
          <button type="button" class="gate-upgrade" @click="$router.push(billingRoute)">Upgrade to Premium</button>
          <button type="button" class="gate-talk" @click="talkToSales">Talk to Sales</button>
        </div>
      </div>
      <div class="gate-image">
        <img src="/img/preflights-img.png" alt="" />
      </div>
    </div>

    <template v-else>
      <div class="jobs-toolbar">
        <div class="group-toggle" role="group" aria-label="Group trend by">
          <button type="button" :class="{ active: groupBy === 'day' }" @click="setGroupBy('day')">Daily</button>
          <button type="button" :class="{ active: groupBy === 'week' }" @click="setGroupBy('week')">Weekly</button>
        </div>
        <button type="button" class="export-btn" @click="downloadCsv">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
          Export CSV
        </button>
      </div>

  <p v-if="error" class="report-error">{{ error }}</p>

  <!-- Summary -->
  <stat-cards :cards="summaryCards" :loading="loading" />

  <!-- Trend -->
  <div class="jr-card" :class="{ 'is-loading': loading }">
    <div class="card-head">
      Activity Over Time
      <span class="card-sub">Comments, proof decisions, status moves and other changes across all jobs</span>
    </div>
    <div class="chart-body">
      <div v-show="hasTrend" class="chart-canvas-wrap">
        <canvas ref="trendCanvas"></canvas>
      </div>
      <div v-if="!loading && !hasTrend" class="card-empty">No activity in this period</div>
    </div>
  </div>

  <!-- Jobs with activity -->
  <div class="jr-card" :class="{ 'is-loading': loading }">
    <div class="card-head">
      Jobs With Activity <span class="count">{{ report.jobs.length }}</span>
      <span class="card-sub">Jobs that received any activity in the period. Stale means nothing has happened in {{ report.staleDays }} days.</span>
    </div>
    <div class="table-wrap">
      <table class="report-table" v-if="report.jobs.length">
        <thead>
          <tr><th>Job</th><th>Status</th><th>Tags</th><th class="num">Comments</th><th class="num">Decisions</th><th class="num">Moves</th><th class="num">Other</th><th>Last activity</th></tr>
        </thead>
        <tbody>
          <tr v-for="j in report.jobs" :key="j.jobId">
            <td class="wrap"><router-link :to="`/job-details/${j.jobId}`" class="job-link"><span class="muted">{{ j.jobNumber }}</span> {{ j.name }}</router-link></td>
            <td>{{ j.statusName }}</td>
            <td><span v-for="t in j.tags" :key="t.id" class="tag-pill">{{ t.name }}</span></td>
            <td class="num">{{ j.comments }}</td>
            <td class="num">{{ j.decisions }}</td>
            <td class="num">{{ j.statusMoves }}</td>
            <td class="num">{{ j.otherChanges }}</td>
            <td>{{ ago(j.lastActivityAt) }} <span v-if="j.stale" class="flag flag-stale">stale</span></td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="!loading" class="card-empty">No jobs had activity in this period</div>
    </div>
  </div>

  <!-- Proofs -->
  <div class="jr-card" :class="{ 'is-loading': loading }">
    <div class="card-head">
      Proofs With Comments Or Decisions <span class="count">{{ report.proofs.length }}</span>
      <span class="card-sub">Linked proofs that received reviewer comments or decisions in the period.</span>
    </div>
    <div class="table-wrap">
      <table class="report-table" v-if="report.proofs.length">
        <thead>
          <tr><th>Proof</th><th>Job</th><th class="num">Comments</th><th>Last comment</th><th class="num">Decisions</th><th>Last decision</th></tr>
        </thead>
        <tbody>
          <tr v-for="p in report.proofs" :key="`${p.proofId}-${p.jobId}`">
            <td class="wrap"><router-link :to="`/proof-item?id=${p.proofId}`" class="job-link">{{ p.proofName }}</router-link></td>
            <td class="wrap"><router-link :to="`/job-details/${p.jobId}`" class="job-link"><span class="muted">{{ p.jobNumber }}</span> {{ p.jobName }}</router-link></td>
            <td class="num">{{ p.comments }}</td>
            <td>{{ when(p.lastCommentAt) }}</td>
            <td class="num">{{ p.decisions }}</td>
            <td>{{ when(p.lastDecisionAt) }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="!loading" class="card-empty">No linked proofs received comments or decisions in this period</div>
    </div>
  </div>

  <!-- Status movement -->
  <div class="jr-card" :class="{ 'is-loading': loading }">
    <div class="card-head">
      Status Movement <span class="count">{{ report.statusMovement.length }}</span>
      <span class="card-sub">Moves in the period and total days spent in each status. Drag-and-drop moves are recorded from {{ when(report.statusHistoryCompleteFrom, true) }} onward.</span>
    </div>
    <div class="table-wrap">
      <table class="report-table" v-if="report.statusMovement.length">
        <thead>
          <tr><th>Job</th><th class="num">Moves</th><th>Current status</th><th class="num">Days in current</th><th>Time in status</th></tr>
        </thead>
        <tbody>
          <tr v-for="m in report.statusMovement" :key="m.jobId">
            <td class="wrap"><router-link :to="`/job-details/${m.jobId}`" class="job-link"><span class="muted">{{ m.jobNumber }}</span> {{ m.name }}</router-link></td>
            <td class="num">{{ m.movesInPeriod }}</td>
            <td>{{ m.currentStatusName }}</td>
            <td class="num">{{ m.daysInCurrentStatus }}</td>
            <td><span v-for="d in m.timeInStatus" :key="d.statusId" class="tag-pill">{{ d.statusName }} {{ d.days }}d</span></td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="!loading" class="card-empty">No status moves in this period</div>
    </div>
  </div>

  <!-- Overdue + by tag -->
  <div class="two-col">
    <div class="jr-card" :class="{ 'is-loading': loading }">
      <div class="card-head">
        Overdue <span class="count">{{ report.overdue.length }}</span>
        <span class="card-sub">Open jobs whose end date has passed, as of today.</span>
      </div>
      <div class="table-wrap">
        <table class="report-table" v-if="report.overdue.length">
          <thead>
            <tr><th>Job</th><th>Status</th><th>End date</th><th class="num">Days overdue</th></tr>
          </thead>
          <tbody>
            <tr v-for="o in report.overdue" :key="o.jobId">
              <td class="wrap"><router-link :to="`/job-details/${o.jobId}`" class="job-link"><span class="muted">{{ o.jobNumber }}</span> {{ o.name }}</router-link></td>
              <td>{{ o.statusName }}</td>
              <td>{{ when(o.endDate, true) }}</td>
              <td class="num"><span class="flag flag-overdue">{{ o.daysOverdue }}</span></td>
            </tr>
          </tbody>
        </table>
        <div v-else-if="!loading" class="card-empty">Nothing is overdue</div>
      </div>
    </div>

    <div class="jr-card" :class="{ 'is-loading': loading }">
      <div class="card-head">
        By Tag <span class="count">{{ report.byTag.length }}</span>
        <span class="card-sub">Open jobs grouped by tag, so you can see whose court work is in and whether it is moving.</span>
      </div>
      <div class="table-wrap">
        <table class="report-table" v-if="report.byTag.length">
          <thead>
            <tr><th>Tag</th><th class="num">Open</th><th class="num">Active</th><th class="num">Stale</th><th class="num">Overdue</th><th>Last activity</th></tr>
          </thead>
          <tbody>
            <tr v-for="t in report.byTag" :key="t.tagId">
              <td><span class="tag-pill">{{ t.tagName }}</span></td>
              <td class="num">{{ t.jobs }}</td>
              <td class="num">{{ t.jobsWithActivity }}</td>
              <td class="num"><span v-if="t.staleJobs" class="flag flag-stale">{{ t.staleJobs }}</span><span v-else>0</span></td>
              <td class="num"><span v-if="t.overdueJobs" class="flag flag-overdue">{{ t.overdueJobs }}</span><span v-else>0</span></td>
              <td>{{ ago(t.lastActivityAt) }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else-if="!loading" class="card-empty">No open jobs are tagged</div>
      </div>
    </div>
  </div>
    </template>
  </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Legend, Tooltip } from "chart.js";
import statCards from "./stat-cards.vue";
import { ROUTES, JOB_FEATURES_LIST } from "../../utils/constants";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Legend, Tooltip);

function emptyReport() {
  return {
    staleDays: 7,
    statusHistoryCompleteFrom: null,
    summary: {},
    trend: [],
    jobs: [],
    proofs: [],
    statusMovement: [],
    overdue: [],
    byTag: [],
  };
}

export default {
  name: "jobs-dashboard",
  components: { statCards },
  props: {
    // { preset, startDate, endDate } as emitted by period-filter
    period: { type: Object, required: true },
    premium: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      error: "",
      groupBy: "day",
      report: emptyReport(),
      chart: null,
      features: JOB_FEATURES_LIST,
      billingRoute: ROUTES.BILLING,
    };
  },
  computed: {
    hasTrend() {
      return (this.report.trend || []).some((b) => b.total > 0);
    },
    summaryCards() {
      const s = this.report.summary || {};
      const n = (v) => (v == null ? "0" : String(v));
      return [
        { number: n(s.totalActions), description: "Total actions", tooltip: "Every comment, decision, status move and job change in the period." },
        { number: n(s.jobsWithActivity), description: "Jobs with activity" },
        { number: n(s.comments), description: "Comments", tooltip: "Comments on jobs and on linked proofs." },
        { number: n(s.decisions), description: "Proof decisions", tooltip: "Approved, approved with changes, or not approved on linked proofs." },
        { number: n(s.statusMoves), description: "Status moves" },
        { number: n(s.overdueJobs), description: "Overdue jobs", tooltip: "Open jobs whose end date has passed." },
        { number: n(s.staleJobs), description: "Stale jobs", tooltip: `Open jobs with no activity in ${this.report.staleDays || 7} days.` },
      ];
    },
  },
  watch: {
    period() {
      if (this.premium) this.load();
    },
    premium: {
      immediate: true,
      handler(premium) {
        if (premium) this.load();
        else this.loading = false;
      },
    },
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  },
  methods: {
    queryOptions() {
      // The API takes calendar dates; "all time" falls back to the job system's first month.
      const start = this.period.startDate ? moment(this.period.startDate).format("YYYY-MM-DD") : "2025-01-01";
      const end = this.period.endDate ? moment(this.period.endDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
      return { start, end, groupBy: this.groupBy };
    },
    async setGroupBy(groupBy) {
      if (this.groupBy === groupBy) return;
      this.groupBy = groupBy;
      await this.load();
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const data = await this.$A.JobService.getReport(this.queryOptions());
        this.report = { ...emptyReport(), ...data };
        this.$nextTick(() => this.renderChart());
      } catch (e) {
        console.error("Failed to load jobs report:", e);
        this.error = "Could not load the report. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    talkToSales() {
      window.open("https://ashoreapp.com/contact", "_blank");
    },
    async downloadCsv() {
      const url = await this.$A.JobService.reportExportUrl(this.queryOptions());
      window.open(url, "_blank");
    },
    renderChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      const canvas = this.$refs.trendCanvas;
      if (!canvas || !this.hasTrend) return;
      const buckets = this.report.trend || [];
      const fmt = this.groupBy === "week" ? "MMM D" : "MMM D";
      const labels = buckets.map((b) => (this.groupBy === "week" ? "Wk of " : "") + moment.utc(b.bucketStart).format(fmt));
      const series = (key, label, color) => ({
        label,
        data: buckets.map((b) => b[key] || 0),
        backgroundColor: color,
        borderRadius: 3,
        stack: "activity",
      });
      this.chart = new Chart(canvas, {
        type: "bar",
        data: {
          labels,
          datasets: [
            series("comments", "Comments", "#019256"),
            series("decisions", "Decisions", "#2b4fb5"),
            series("statusMoves", "Status moves", "#d9b116"),
            series("otherChanges", "Other changes", "#9ca3af"),
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "bottom", labels: { boxWidth: 10, usePointStyle: true } } },
          scales: {
            x: { stacked: true, grid: { display: false }, ticks: { maxTicksLimit: 14 } },
            y: { stacked: true, beginAtZero: true, ticks: { precision: 0 }, grid: { color: "#f3f4f6" } },
          },
        },
      });
    },
    when(value, dateOnly = false) {
      if (!value) return "";
      return moment.utc(value).local().format(dateOnly ? "MMM D, YYYY" : "MMM D, YYYY h:mm a");
    },
    ago(value) {
      if (!value) return "never";
      return moment.utc(value).fromNow();
    },
  },
};
</script>

<style scoped>
.jobs-dashboard {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.jobs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: -8px;
}
.group-toggle {
  display: inline-flex;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  overflow: hidden;
  height: 36px;
  background: #fff;
}
.group-toggle button {
  border: 0;
  background: transparent;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  padding: 0 12px;
  cursor: pointer;
}
.group-toggle button.active {
  background: #111827;
  color: #fff;
}
.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.export-btn:hover {
  background: #f9fafb;
  border-color: rgba(15, 23, 42, 0.25);
}
.report-error {
  color: #b42318;
  background: #fdeaec;
  padding: 10px 14px;
  border-radius: 8px;
  margin: 0;
}

/* Card */
.jr-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
  display: flex;
  flex-direction: column;
  min-width: 0;
  transition: opacity 150ms ease;
}
.jr-card.is-loading {
  opacity: 0.45;
}
.card-head {
  padding: 14px 20px;
  background: #fbfbfc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px 12px 0 0;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  flex-shrink: 0;
}
.card-head .count {
  display: inline-block;
  margin-left: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: #eef0f3;
  border-radius: 999px;
  padding: 1px 8px;
  vertical-align: middle;
}
.card-sub {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}
.chart-body {
  padding: 16px 20px;
}
.chart-canvas-wrap {
  position: relative;
  height: 320px;
  width: 100%;
}
.card-empty {
  padding: 48px 20px;
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: #9ca3af;
}
.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;
}
.two-col > * {
  min-width: 0;
}

/* Table rows match the overview list rows */
.table-wrap {
  overflow-x: auto;
}
.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}
.report-table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 10px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  white-space: nowrap;
}
.report-table td {
  padding: 13px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  color: #4b5563;
  vertical-align: middle;
  white-space: nowrap;
}
.report-table td.wrap {
  white-space: normal;
  min-width: 220px;
}
.report-table tbody tr {
  transition: background-color 120ms ease;
}
.report-table tbody tr:hover {
  background: #f9f9fa;
}
.report-table tr:last-child td {
  border-bottom: 0;
}
.report-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.job-link {
  color: #0f172a;
  text-decoration: none;
  font-weight: 400;
  transition: color 120ms ease;
}
.job-link:hover {
  color: #019256;
  text-decoration: none;
}
.muted {
  color: #9ca3af;
}
.tag-pill {
  display: inline-block;
  background: #f3f4f6;
  color: #4b5563;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  margin: 0 4px 2px 0;
  white-space: nowrap;
}
.flag {
  display: inline-block;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
}
.flag-stale {
  background: #fff4e5;
  color: #b45309;
}
.flag-overdue {
  background: #fdeaec;
  color: #b42318;
}

@media (max-width: 991px) {
  .two-col {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 575px) {
  .chart-canvas-wrap {
    height: 240px;
  }
  .report-table th,
  .report-table td {
    padding: 10px 14px;
  }
}

/* Gated state */
.gate-card {
  flex-direction: row;
  align-items: center;
  overflow: hidden;
}
.gate-body {
  flex: 1;
  min-width: 0;
  padding: 40px 44px;
}
.gate-eyebrow {
  font-size: 11px;
  font-weight: 700;
  color: #019256;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.gate-title {
  font-size: 24px;
  line-height: 30px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #111827;
  margin: 0 0 8px;
}
.gate-text {
  font-size: 14px;
  color: #4b5563;
  margin: 0 0 18px;
}
.gate-features {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
}
.gate-features li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
}
.gate-features img {
  width: 18px;
  height: 18px;
}
.gate-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.gate-upgrade {
  background: #019256;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.gate-upgrade:hover {
  background: #017b46;
}
.gate-talk {
  background: #fff;
  color: #374151;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.gate-talk:hover {
  background: #f9fafb;
}
.gate-image {
  position: relative;
  flex: 0 0 40%;
  max-width: 480px;
  align-self: stretch;
  background: #f4f5f7;
}
.gate-image img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
@media (max-width: 991px) {
  .gate-card {
    flex-direction: column;
  }
  .gate-image {
    display: none;
  }
  .gate-body {
    padding: 28px 24px;
  }
}
</style>
